// Auth/session-related keys, matched by suffix, regardless of casing style (e.g. `authToken` and `auth_token` both match).
const AUTH_SUFFIXES_TO_REDACT = ['password', '_tk', 'token', 'secret', 'samlresponse'];

// PII keys, matched by suffix, regardless of casing style (e.g. `firstName` and `first_name` both match).
const PII_SUFFIXES_TO_REDACT = [
	'email',
	'emailAddress',
	'emailAddresses',
	'firstName',
	'lastName',
	'displayName',
	'otpCode',
];

// Keys matched in full (not just by suffix) regardless of casing style.
// `address` is exact-match rather than a PII suffix so it doesn't also blank `ipAddress`/`ip_address`,
// which carry no PII but are useful for abuse investigation.
const EXACT_KEYS_TO_REDACT = [
	'cookies',
	'cookie',
	'set-cookie',
	'_change_session',
	'address',
	// `rt` is the decision-maker self-service response token (`/p/:slug/dm/respond?rt=…`), a bearer
	// credential. It must be exact-match: as a suffix, `rt$` would also blank `sort`, `start`, `cart`.
	// Because `redactUrlParams` tests param names against this same regex, this covers `rt` both as an
	// object key and as a query param inside a URL-shaped value.
	'rt',
];

const toSnakeCase = (key: string): string => key.replace(/([A-Z])/g, '_$1').toLowerCase();

// eslint-disable-next-line security/detect-non-literal-regexp
export const VALUES_TO_REDACT_REGEX = new RegExp(
	`(${[
		...new Set(
			[...AUTH_SUFFIXES_TO_REDACT, ...PII_SUFFIXES_TO_REDACT].flatMap((suffix) => [
				suffix.toLowerCase(),
				toSnakeCase(suffix),
			]),
		).values(),
	]
		.map((suffix) => `${suffix}$`)
		.concat(EXACT_KEYS_TO_REDACT.map((key) => `^${key}$`))
		.join('|')})`,
	'i',
);

// `displayName`/`display_name` isn't PII-specific to people — Country, LocalPage, ScopedStaffRole, etc.
// all expose it too, with no PII. See `isPersonDisplayName` for how this is scoped down to person records.
export const DISPLAY_NAME_KEY_REGEX = /(displayname|display_name)$/i;

// Matches keys whose value is expected to hold a URL, regardless of casing style, so query-param
// redaction can be applied to the string leaf found under them (e.g. `url`, `originalUrl`,
// `requestUrl`, `redirectUrl`, `href`, `uri`, and their snake_case forms). The underscore in
// snake_case forms doesn't interfere with a plain suffix match (`original_url` still ends in `url`).
// `referer` (the HTTP header's spelling) and `referrer` hold a full URL but end in neither `url`
// nor `uri`, so they're listed explicitly — otherwise a credential in the referring page's query
// string (e.g. `?rt=…` on the DM response page) is logged verbatim on every request it triggers.
export const URL_KEY_REGEX = /(?:url|uri|href|referr?er)$/i;
